// frontend/src/hooks/usePortfolio.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { Portfolio, BlockData, UpdatePortfolioRequest } from '../types/Portfolio';
import portfolioService from '../services/portfolioService';
import { getBlockByType } from '../utils/blockRegistry';

interface UsePortfolioReturn {
  portfolio: Portfolio | null;
  loading: boolean;
  error: string | null;
  hasUnsavedChanges: boolean;
  lastSaved: Date | null;
  saving: boolean;
  selectedBlockId: string | null;
  
  // Block operations
  addBlock: (type: string) => void;
  removeBlock: (id: string) => void;
  updateBlock: (id: string, data: any) => void;
  reorderBlocks: (newOrder: BlockData[]) => void;
  toggleBlockEnabled: (id: string) => void;
  selectBlock: (id: string | null) => void;
  
  // Portfolio operations
  updatePortfolioMeta: (data: Partial<UpdatePortfolioRequest>) => void;
  save: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const usePortfolio = (portfolioId: string): UsePortfolioReturn => {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const portfolioRef = useRef<Portfolio | null>(null);

  // Keep ref updated with latest portfolio
  useEffect(() => {
    portfolioRef.current = portfolio;
  }, [portfolio]);

  // Load portfolio
  const loadPortfolio = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await portfolioService.getById(portfolioId);
      setPortfolio(data);
      
      if (data.lastModified) {
        const parsedDate = new Date(data.lastModified);
        if (!isNaN(parsedDate.getTime())) {
          setLastSaved(parsedDate);
        } else {
          setLastSaved(null);
        }
      } else {
        setLastSaved(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load portfolio');
      console.error('Error loading portfolio:', err);
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  // Initial load
  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  // Auto-save timer (every 60 seconds if changes exist)
  useEffect(() => {
    if (hasUnsavedChanges && portfolio) {
      autoSaveTimerRef.current = setTimeout(() => {
        save();
      }, 60000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, portfolio]);

const save = useCallback(async () => {
  return new Promise<void>((resolve, reject) => {
    setPortfolio((currentPortfolio) => {
      if (!currentPortfolio) {
        resolve();
        return currentPortfolio;
      }

      // Use the current state to save
      (async () => {
        try {
          setSaving(true);
          
          const portfolioId = (currentPortfolio as any)._id || currentPortfolio.id;
          
          const updateData: UpdatePortfolioRequest = {
            title: currentPortfolio.title,
            description: currentPortfolio.description,
            investorContext: currentPortfolio.investorContext,
            sections: currentPortfolio.sections,
            theme: currentPortfolio.theme,
            meta: currentPortfolio.meta
          };

          await portfolioService.update(portfolioId, updateData);
          setHasUnsavedChanges(false);
          setLastSaved(new Date());
          resolve();
        } catch (err: any) {
          console.error('Error saving portfolio:', err);
          setError(err.message || 'Failed to save portfolio');
          reject(err);
        } finally {
          setSaving(false);
        }
      })();

      return currentPortfolio;
    });
  });
}, []);

  // Add block
  const addBlock = useCallback((type: string) => {
    if (!portfolio) return;

    const blockConfig = getBlockByType(type as any);
    if (!blockConfig) {
      console.error('Block type not found:', type);
      return;
    }

    const newBlock: BlockData = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: type as any,
      order: portfolio.sections.length,
      enabled: true,
      data: blockConfig.defaultData
    };

    setPortfolio({
      ...portfolio,
      sections: [...portfolio.sections, newBlock]
    });
    setHasUnsavedChanges(true);
    setSelectedBlockId(newBlock.id);
  }, [portfolio]);

  // Remove block
  const removeBlock = useCallback((id: string) => {
    if (!portfolio) return;

    setPortfolio({
      ...portfolio,
      sections: portfolio.sections.filter(block => block.id !== id)
    });
    setHasUnsavedChanges(true);
    
    if (selectedBlockId === id) {
      setSelectedBlockId(null);
    }
  }, [portfolio, selectedBlockId]);

  // Update block
 const updateBlock = useCallback((id: string, data: any) => {
  console.log('usePortfolio updateBlock called with id:', id, 'data:', data);
  if (!portfolio) return;

  const updatedSections = portfolio.sections.map(block =>
    block.id === id
      ? { ...block, data }
      : block
  );
  
  console.log('usePortfolio updateBlock - updatedSections:', updatedSections);

  setPortfolio({
    ...portfolio,
    sections: updatedSections
  });
  setHasUnsavedChanges(true);
}, [portfolio]);

  // Reorder blocks
  const reorderBlocks = useCallback((newOrder: BlockData[]) => {
    if (!portfolio) return;

    const reorderedSections = newOrder.map((block, index) => ({
      ...block,
      order: index
    }));

    setPortfolio({
      ...portfolio,
      sections: reorderedSections
    });
    setHasUnsavedChanges(true);
  }, [portfolio]);

  // Toggle block enabled
  const toggleBlockEnabled = useCallback((id: string) => {
    if (!portfolio) return;

    setPortfolio({
      ...portfolio,
      sections: portfolio.sections.map(block =>
        block.id === id
          ? { ...block, enabled: !block.enabled }
          : block
      )
    });
    setHasUnsavedChanges(true);
  }, [portfolio]);

  // Select block
  const selectBlock = useCallback((id: string | null) => {
    setSelectedBlockId(id);
  }, []);

  // Update portfolio metadata
  const updatePortfolioMeta = useCallback((data: Partial<UpdatePortfolioRequest>) => {
    if (!portfolio) return;

    const updatedPortfolio: Portfolio = {
      ...portfolio,
      ...(data.title && { title: data.title }),
      ...(data.description !== undefined && { description: data.description }),
      ...(data.investorContext && { 
        investorContext: { ...portfolio.investorContext, ...data.investorContext } 
      }),
      ...(data.sections && { sections: data.sections }),
      ...(data.theme && { theme: { ...portfolio.theme, ...data.theme } }),
      ...(data.meta && { meta: { ...portfolio.meta, ...data.meta } })
    };

    setPortfolio(updatedPortfolio);
    setHasUnsavedChanges(true);
  }, [portfolio]);

  // Refresh portfolio
  const refresh = useCallback(async () => {
    await loadPortfolio();
  }, [loadPortfolio]);

  return {
    portfolio,
    loading,
    error,
    hasUnsavedChanges,
    lastSaved,
    saving,
    selectedBlockId,
    
    addBlock,
    removeBlock,
    updateBlock,
    reorderBlocks,
    toggleBlockEnabled,
    selectBlock,
    
    updatePortfolioMeta,
    save,
    refresh
  };
};